import React, { useState, useEffect, useRef, useCallback } from 'react';
import { renderMarkdown } from '../../utils/markdown';
import { authService } from '../../services/authService';
import { fetchSuggestions, sendChatMessage, ChatMessage } from '../../services/donKingService';
import './AiAssistantModal.css';

interface Props {
  machineId: string;
  machineDescription?: string;
  problemDescription?: string;
  onClose: () => void;
}

interface DisplayMessage {
  role: 'user' | 'model';
  text: string;
}

const AiAssistantModal: React.FC<Props> = ({
  machineId,
  machineDescription,
  problemDescription,
  onClose,
}) => {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Load initial suggestions on mount.
  // Debounce (50ms) ensures the fetch never starts during React StrictMode's
  // mount→cleanup→remount cycle (~<10ms), preventing duplicate Gemini calls.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const token = authService.getAccessToken();
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const suggestions = await fetchSuggestions(machineId, token, problemDescription, controller.signal);
        setMessages([{ role: 'model', text: suggestions }]);
      } catch (e) {
        if ((e as { name?: string }).name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Errore nel caricamento dei suggerimenti');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 50);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [machineId, problemDescription]);

  // Focus input after suggestions load
  useEffect(() => {
    if (!loading) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [loading]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const token = authService.getAccessToken();
    if (!token) return;

    const userMsg: DisplayMessage = { role: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    // Build history for the API (exclude the initial auto-generated message from history,
    // as it will be re-generated server-side from context)
    const history: ChatMessage[] = messages
      .slice(1) // skip initial suggestions block
      .concat(userMsg)
      .map(m => ({ role: m.role, parts: [m.text] }));

    try {
      const reply = await sendChatMessage(
        machineId,
        token,
        text,
        history,
        problemDescription
      );
      setMessages(prev => [...prev, { role: 'model', text: reply }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nella risposta');
    } finally {
      setLoading(false);
    }
  }, [input, loading, machineId, messages, problemDescription]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  return (
    <div className="ai-assistant-overlay" onClick={handleOverlayClick}>
      <div className="ai-assistant-modal" role="dialog" aria-label="AI Assistant">
        {/* Header */}
        <div className="ai-modal-header">
          <span className="material-icons">auto_awesome</span>
          <span className="ai-modal-header-title">
            Assistente AI{machineDescription ? ` — ${machineDescription}` : ''}
          </span>
          <button className="ai-modal-close" onClick={onClose} aria-label="Chiudi">
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* Messages */}
        <div className="ai-messages">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`ai-message ${msg.role}`}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
            />
          ))}

          {loading && (
            <div className="ai-loading">
              <div className="ai-loading-dots">
                <span /><span /><span />
              </div>
            </div>
          )}

          {error && <div className="ai-error">{error}</div>}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="ai-input-row">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Fai una domanda..."
            disabled={loading}
          />
          <button
            className="ai-send-btn"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            aria-label="Invia"
          >
            <span className="material-icons">send</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiAssistantModal;
